// pages/master-data/vendor/VendorAddEdit.jsx
'use client';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, TextField, CircularProgress, Stack, Divider, Button } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { getVendorById, createVendor, updateVendor } from '../../../stores/vendorStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import { useBusinessTypeList } from '../../../stores/businessTypeStore';
import BusinessTypeAddEdit from '../business-type/BusinessTypeAddEdit';
import usePickerPagination from '../../../hooks/usePickerPagination';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import PickerField from '../../../components/PickerField';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';

// Nilai default form kosong untuk reset
const DEFAULT_EMPTY_VALUES = {
  id: null,
  company: null,
  code: '',
  name: '',
  phone: '',
  fax: '',
  business_type: null,
  website: '',
  description: '',
  cp_name_1: '',
  cp_phone_1: '',
  cp_email_1: '',
  cp_title_1: '',
  npwp_no: '',
  npwp_address: '',
  username: '',
  password: '',
  role_name: ''
};

const VendorAddEdit = forwardRef(({ open, id, onClose, onSuccess, onError, companyOptions: companyOptionsFromParent = [] }, ref) => {
  const { t } = useLanguage();
  const notify = useNotifier();

  const [initialValues, setInitialValues] = useState(DEFAULT_EMPTY_VALUES);
  const [loading, setLoading] = useState(false);
  const formikRef = useRef(null);

  // Hook untuk mengambil list Company
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const mappedCompanyOptions = companyPickerParams.options;

  // Hook untuk mengambil list Business Type
  const businessTypePickerParams = usePickerPagination({
    fetchHook: useBusinessTypeList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => formikRef.current?.submitForm?.()
  }));

  // Efek untuk reset form atau fetch data berdasarkan ID saat modal dibuka
  useEffect(() => {
    let mounted = true;

    const initializeForm = async () => {
      if (!open) return;

      if (!id) {
        // Mode Add Baru
        setInitialValues({ ...DEFAULT_EMPTY_VALUES });

        try {
          const storedCompanyId = localStorage.getItem('companyId');
          if (storedCompanyId && mappedCompanyOptions.length > 0) {
            const matched = mappedCompanyOptions.find((opt) => String(opt.value) === String(storedCompanyId));
            const valueToSet = matched ? matched.value : storedCompanyId;
            setInitialValues((prev) => ({ ...prev, company: valueToSet }));
          }
        } catch (err) {
          console.warn('init company dari localStorage gagal', err);
        }
        return;
      }

      // Mode Edit
      setLoading(true);
      try {
        const response = await getVendorById(id);
        if (!mounted) return;

        // PERBAIKAN: Cek spesifik letak data (response.data jika dibungkus interceptor)
        // Kadang backend nge-wrap lagi didalam object "data", jadi kita ambil terdalam
        const vendorData = response?.data?.data || response?.data || response;

        if (vendorData) {
          setInitialValues({
            id: vendorData.id ?? null,
            // Format ID ke string agar matching dengan option di PickerField
            company: (vendorData.company ?? vendorData.company_id) != null ? String(vendorData.company ?? vendorData.company_id) : null,
            code: vendorData.code ?? '',
            name: vendorData.name ?? '',
            phone: vendorData.phone ?? '',
            fax: vendorData.fax ?? '',
            business_type: vendorData.business_type != null ? String(vendorData.business_type) : null,
            website: vendorData.website ?? '',
            description: vendorData.description ?? '',
            cp_name_1: vendorData.cp_name_1 ?? '',
            cp_phone_1: vendorData.cp_phone_1 ?? '',
            cp_email_1: vendorData.cp_email_1 ?? '',
            cp_title_1: vendorData.cp_title_1 ?? '',
            npwp_no: vendorData.npwp_no ?? '',
            npwp_address: vendorData.npwp_address ?? '',
            username: vendorData.username ?? '',
            password: '',
            role_name: vendorData.role_name ?? ''
          });
        }
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal memuat data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initializeForm();

    return () => {
      mounted = false;
    };
  }, [id, open, mappedCompanyOptions, notify, onError]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required('Company is required'),
    code: Yup.string().required('Code is required'),
    name: Yup.string().required('Name required'),
    business_type: Yup.mixed().required('Bussines Type is required'),
    cp_name_1: Yup.string().max(20, 'PIC Name maximum 20 characters')
  });

  useEffect(() => {
    if (!open) {
      formikRef.current?.resetForm();
    }
  }, [open]);

  const normalizeNumber = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  };

  const buildPayload = (values) => {
    const payload = {
      // Pastikan merubah tipe data company & business_type ke integer (bila disyaratkan oleh backend)
      company: normalizeNumber(values.company),
      code: values.code ?? '',
      name: formatUpperCase(values.name) ?? '',
      phone: values.phone ?? '',
      fax: values.fax ?? '',
      business_type: normalizeNumber(values.business_type) ?? null,
      website: values.website ?? '',
      description: values.description ?? '',
      cp_name_1: values.cp_name_1 ?? '',
      cp_phone_1: values.cp_phone_1 ?? '',
      cp_email_1: values.cp_email_1 ?? '',
      cp_title_1: values.cp_title_1 ?? '',
      npwp_no: values.npwp_no ?? '',
      npwp_address: values.npwp_address ?? '',
      username: values.username ?? '',
      password: values.password ?? '',
      role_name: values.role_name ?? ''
    };

    if (id || values.id) payload.id = id ?? values.id;
    return payload;
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const payload = buildPayload(values);
      let response;

      if (id) {
        response = await updateVendor(id, payload);
      } else {
        response = await createVendor(payload);
      }

      if (response?.code === 400 || response?.status === false || response?.error) {
        throw response;
      }

      notify({ message: id ? 'Updated successfully' : 'Created successfully', severity: 'success' });
      resetForm();
      onClose && onClose();
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, touched, errors, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting }) => (
        <FormDialog
          open={open}
          onClose={onClose}
          title={id ? t('vendor.edit', 'Edit Vendor') : t('vendor.add', 'Add Vendor')}
          fullScreenMode={true}
          fullHeight={false}
          actions={
            <>
              <Button
                variant="contained"
                type="submit"
                form="vendor-form"
                disabled={isSubmitting || loading}
                sx={{ backgroundColor: '#0072DA', textTransform: 'none' }}
                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
              </Button>
              <Button onClick={onClose} disableRipple disableElevation variant="outlined" sx={{ textTransform: 'none' }}>
                {t('buttonTitle.cancel', 'Cancel')}
              </Button>
            </>
          }
        >
          {loading || companyPickerParams.isLoading || businessTypePickerParams.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: '200px', alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
              <form id="vendor-form" onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                  {/* KOLOM KIRI */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack spacing={2} sx={{ width: '100%' }}>
                      <PickerField
                        label={`${t('general.company', 'Company')} *`}
                        name="company"
                        value={values.company ?? null}
                        {...companyPickerParams}
                        onChange={(val) => {
                          const v = typeof val === 'object' && val !== null ? val.value : val;
                          setFieldValue('company', v);
                          try {
                            if (v !== null && v !== undefined && v !== '') {
                              localStorage.setItem('companyId', String(v));
                            }
                          } catch {}
                        }}
                        error={touched.company && !!errors.company}
                        helperText={touched.company && errors.company}
                      />

                      <TextField
                        label={`${t('vendor.code', 'Code')} *`}
                        name="code"
                        value={values.code ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        error={touched.code && !!errors.code}
                        helperText={touched.code && errors.code}
                      />

                      <TextField
                        label={`${t('vendor.name', 'Name')} *`}
                        name="name"
                        value={values.name ?? ''}
                        onChange={(e) => {
                          setFieldValue('name', formatUpperCase(e.target.value));
                        }}
                        onBlur={handleBlur}
                        fullWidth
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                      />

                      <TextField
                        label={t('vendor.phone', 'Phone No.')}
                        name="phone"
                        value={values.phone ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />

                      <TextField
                        label={t('vendor.fax', 'Fax')}
                        name="fax"
                        value={values.fax ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />

                      <PickerField
                        label={`${t('vendor.businessType', 'Business Type')} *`}
                        name="business_type"
                        value={values.business_type ?? null}
                        {...businessTypePickerParams}
                        onChange={(val) => {
                          const v = typeof val === 'object' && val !== null ? val.value : val;
                          setFieldValue('business_type', v);
                        }}
                        allowAdd
                        AddComponent={BusinessTypeAddEdit}
                        error={touched.business_type && !!errors.business_type}
                        helperText={touched.business_type && errors.business_type}
                      />

                      <TextField
                        label={t('vendor.website', 'Website')}
                        name="website"
                        value={values.website ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />

                      <TextField
                        label={t('general.description', 'Description')}
                        name="description"
                        value={values.description ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        minRows={3}
                        fullWidth
                      />
                    </Stack>
                  </Box>

                  {/* KOLOM KANAN */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack spacing={2} sx={{ width: '100%' }}>
                      <TextField
                        label={t('vendor.picName', 'PIC Name')}
                        name="cp_name_1"
                        value={values.cp_name_1 ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                      <TextField
                        label={t('vendor.picPhone', 'PIC Phone')}
                        name="cp_phone_1"
                        value={values.cp_phone_1 ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                      <TextField
                        label={t('vendor.picEmail', 'PIC Email')}
                        name="cp_email_1"
                        value={values.cp_email_1 ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                      <TextField
                        label={t('vendor.picTitle', 'PIC Title')}
                        name="cp_title_1"
                        value={values.cp_title_1 ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />

                      <Divider />

                      <TextField
                        label={t('vendor.npwpNumber', 'NPWP Number')}
                        name="npwp_no"
                        value={values.npwp_no ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                      <TextField
                        label={t('vendor.npwpAddress', 'NPWP Address')}
                        name="npwp_address"
                        value={values.npwp_address ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />

                      <Divider />

                      <TextField
                        label={t('vendor.username', 'Username')}
                        name="username"
                        value={values.username ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                      <TextField
                        label={t('vendor.password', 'Password')}
                        name="password"
                        type="password"
                        value={values.password ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                      <TextField
                        label={t('vendor.roleName', 'Role Name')}
                        name="role_name"
                        value={values.role_name ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Stack>
                  </Box>
                </Box>
              </form>
            </Box>
          )}
        </FormDialog>
      )}
    </Formik>
  );
});

export default VendorAddEdit;
